"use client";

import * as React from "react";
import { Bookmark, MessageSquare } from "lucide-react";
import { useToggleToast } from "@/lib/toast";
import { Link } from "@/i18n/routing";
import { ARTICLE_DETAIL_ROUTE, COMMENTS_OPEN_QUERY } from "@/lib/routes";

const ACCENT = "#259c73";
/**
 * Fill for the SAVED bookmark — deliberately lighter than the `ACCENT` stroke.
 * Filling with the stroke colour turned the icon into a solid green blob with no
 * readable outline; QA asked for "a colour lighter than the current stroke
 * colour" so the saved state stays legible as a bookmark.
 */
const ACCENT_FILL = "#8ccbb4";

interface MetaRowProps {
  time: string;
  comments?: number;
  onBookmarkChange?: (next: boolean) => void;
  /** Override the default navigation. When omitted the comment control links to
   *  the article with its comment drawer already open. */
  onCommentClick?: () => void;
  initialBookmarked?: boolean;
  /** Article this row belongs to; the comment control links here. */
  href?: string;
}

const COMMENT_CLASS =
  "inline-flex items-center gap-[4px] rounded-sm outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[#259c73]/50";

/** Comment icon + count. Sizes bumped alongside the bookmark — QA asked for
 *  both icon buttons to be slightly larger. */
function CommentGlyph({ count }: { count: number }) {
  return (
    <>
      <MessageSquare className="size-[18px]" strokeWidth={1.4} color={ACCENT} />
      <span
        style={{
          fontFamily: "Roboto, sans-serif",
          fontSize: "13px",
          lineHeight: "20px",
          color: ACCENT,
        }}
      >
        {count}
      </span>
    </>
  );
}

export function MetaRow({
  time,
  comments,
  onBookmarkChange,
  onCommentClick,
  initialBookmarked = false,
  href,
}: MetaRowProps) {
  const [bookmarked, setBookmarked] = React.useState(initialBookmarked);
  const notify = useToggleToast();

  // These buttons frequently sit INSIDE a card-wide <a> (hero-section wraps its
  // whole main column in one, and NewsCard/ArticleItemCard now link their image
  // and headline). Without preventDefault/stopPropagation the click bubbles to
  // that anchor and navigates instead of bookmarking — which is exactly the QA
  // report "加入書籤會直接挑轉去到嗰一片文章 做法错误". Same treatment as
  // components/columns/authors/authors-grid.tsx, which already got this right.
  const toggleBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Computed OUTSIDE the updater: React double-invokes updaters under
    // StrictMode, which fired the toast twice.
    const next = !bookmarked;
    setBookmarked(next);
    onBookmarkChange?.(next);
    notify.bookmark(next);
  };

  const handleCommentClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onCommentClick?.();
  };

  return (
    <div className="flex items-center justify-between w-full">
      {/* 20px line box, matching the icon buttons beside it.
          `items-center` was already centring the two, but a 14px text box
          against a 20px icon box leaves the glyphs sitting visibly low — the
          boxes were centred, the ink was not. Giving both the same line height
          makes the alignment true rather than merely arithmetic. */}
      <span
        className="inline-flex items-center"
        style={{
          fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
          fontSize: "12px",
          lineHeight: "20px",
          minHeight: "20px",
          color: "#999",
        }}
      >
        {time}
      </span>
      <span className="flex items-center gap-[16px] pl-[16px]">
        <button
          type="button"
          aria-label={bookmarked ? "已收藏" : "收藏"}
          aria-pressed={bookmarked}
          onClick={toggleBookmark}
          className="inline-flex items-center justify-center size-[20px] rounded-sm outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[#259c73]/50"
        >
          <Bookmark
            className="size-[20px]"
            strokeWidth={1.4}
            color={ACCENT}
            fill={bookmarked ? ACCENT_FILL : "none"}
          />
        </button>

        {comments !== undefined &&
          (onCommentClick ? (
            <button
              type="button"
              aria-label={`評論 ${comments}`}
              onClick={handleCommentClick}
              className={COMMENT_CLASS}
            >
              <CommentGlyph count={comments} />
            </button>
          ) : (
            // QA: the comment control should take the reader to the article with
            // its comment drawer already open. `?comments=open` is read by the
            // article page (see COMMENTS_OPEN_QUERY).
            <Link
              href={`${href ?? ARTICLE_DETAIL_ROUTE}?${COMMENTS_OPEN_QUERY}=open`}
              aria-label={`評論 ${comments}`}
              onClick={(e) => e.stopPropagation()}
              className={COMMENT_CLASS}
            >
              <CommentGlyph count={comments} />
            </Link>
          ))}
      </span>
    </div>
  );
}
